import { FAKE_COMPONENT_VALUE_LOC_KEY } from "@multiweb/reactComponents";
import { setupBem } from "Global/Dom/bem";
import { ITextBuilder } from "../../../../Global/Base/Builders/ITextBuilder";
import { Enum, EnumType } from "../../../../OW.Remoting/Remoting/DataTypes/Enums";
import { EnumDescription } from "../../../../OW.Remoting/Remoting/DataTypes/IEnums";
import { Localization } from "../../../../OW.Remoting/Remoting/Localization";
import { OWManager } from "../../../../OW.Remoting/Remoting/OWManager";
import { createElement } from "../../../UI.ElementarControls/Utils/CustomElementUtils";
import { FrameContext } from "../UIManagerInterfaces";
import { FakeComponentValueState } from "./ControlEnums";
import { ControlParams, IHasFrameContext, IMultiEdit } from "./ControlInterfaces";
import { EditableControl } from "./EditableControl";

const bemClass = setupBem("combobox");

export class ComboBox extends EditableControl implements IMultiEdit, IHasFrameContext {
    /**
     * Hodnota pro možnost reprezentující hromadnou editaci. Tato hodnota slouží pouze pro rozlišení možnosti a neměla by se používat nikde mimo ComboBox.
     */
    private static readonly EMPTY_OPTION_VALUE = "-1";
    private static readonly PLACEHOLDER_OPTION_VALUE = "-2";
    private static readonly MULTI_EDIT_OPTION_VALUE = "-3";

    //#region Fields
    public override innerElement: HTMLSelectElement;
    public disabledFixElement: HTMLDivElement; // Element vytvoren z duvodu toho,ze pokud je combobox disabled, neodesila udalosti o kliku a tedy po kliku na nej se nevstoupi do editace

    private _enumName: string;
    private _hideValues: string;
    /**Seznam skrytých hodnot, které nemají být v editaci vidět*/
    private _hiddenValues: EnumDescription[];
    /**Seznam ID všech prvků*/
    private _comboListIds: number[] = [];
    /**Seznam ID skrytých prvků*/
    private _hiddenIds: number[] = [];
    /**
     * Data enumu, výčtového typu, který se má zobrazovat jako jednotlivé položky ComboBoxu
     */
    private _data: EnumType;
    /**
     * Lokalizované popisy všech položek enumu. Klíčem je číselná hodnota položky enumu a hodnotou je lokalizovaný název položky.
     */
    private _enumLocalizations: Map<number, string>;
    // doplneno pro pripad, kdy je comboboxu predana hodnota pred jeho nactenim, obsahuje asynchronni volani na vytvareni option, ne vzdy se to stihlo
    private _beforeLoadValue: Enum;
    private _hasBeforeLoadValue: boolean = false;
    private _isLoaded: boolean = false;
    /**
     * Prázdný element typu option - prázdný záznam. Při required ComboBoxu se nezobrazí
     */
    private _emptyOption: HTMLOptionElement = createElement({ type: "option", attrs: { value: ComboBox.EMPTY_OPTION_VALUE } }) as HTMLOptionElement;
    private _placeholderOption: HTMLOptionElement = createElement({ type: "option", attrs: { value: ComboBox.PLACEHOLDER_OPTION_VALUE } }) as HTMLOptionElement;
    private _multiEditOption: HTMLOptionElement = createElement({ type: "option", attrs: { value: ComboBox.MULTI_EDIT_OPTION_VALUE } }) as HTMLOptionElement;

    /**
     * Interní příznak, zda je zobrazen prázdný záznam
     */
    private __showEmptyOption: boolean = false;
    /** Příznak, zda je aktivní hromadná editace. */
    private _multiEditActive: boolean = false;
    /** Příznak, zda je zobrazena možnost reprezentující "Více hodnot" v hromadné editaci. */
    private _multiEditOptionActive: boolean = false;
    /** Příznak, zda byla provedena interakce s ComboBoxem v průběhu hromadné editace. */
    private __touched: boolean = false;
    /**
     * Řeší problém při změně výběru. Při setování controlValue je potřeba rozlišit, jestli hodnota null přišla v reakci na stisk backspace nebo to je načtená hodnota z db a podle toho vyrenderovat fake komponentu.
     */
    private _valueState: FakeComponentValueState;

    private _frameContext: FrameContext | undefined = undefined;

    //#endregion Fields
    public get frameContext(): FrameContext | undefined {
        return this._frameContext;
    }

    public set frameContext(value: FrameContext | undefined) {
        this._frameContext = value;
    }

    public get innerValue(): string {
        return this.innerElement.value;
    }

    public set innerValue(value: string) {
        if (this._multiEditActive && this.innerElement.value == ComboBox.MULTI_EDIT_OPTION_VALUE) {
            return;
        }
        if ([ComboBox.EMPTY_OPTION_VALUE, "", null, undefined].includes(value)) {
            value = ComboBox.PLACEHOLDER_OPTION_VALUE;
        }
        this.innerElement.value = value;
        this._updateEmptyState();
    }

    //#region Properties
    public get touched(): boolean {
        return this._touched;
    }

    public override get controlValue(): Enum {
        const value = this._getSelectedEnum();
        if (value != null && this.isEditing && this.enabled && this._hiddenIds?.includes(value.valueOf())) {
            return null;
        }
        return value;
    }

    public override set controlValue(id: Enum) {
        if (!this._isLoaded) {
            this._beforeLoadValue = id;
            this._hasBeforeLoadValue = true; // Může nastat situace, kdy je potřeba nastavit i null, proto je používána další proměnná
            return;
        }

        // Všechny záznamy lze zobrazit/nastavit jen v režimu needitace, jinak je třeba je omezit přes seznam skrytých hodnot
        if (id != null && this._comboListIds.includes(id.valueOf()) && ((!this.isEditing || !this.enabled) || !this._hiddenIds.includes(id.valueOf()))) {
            this.innerValue = id + "";
        } else {
            this.innerValue = null;
        }
        if (id)
            this._valueState = FakeComponentValueState.userChanged;
        //komponentu je potřeba nastavit jako změněnou pokud uživatel smazal hodnotu přes fake nebo nastavil konkrétní hodnotu
        if (this._valueState !== FakeComponentValueState.notSet && !this._touched)
            this._touched = true;
    }

    public override get displayedValue(): ITextBuilder {
        return this.controlValue?.toLocaleString() ?? "";
    }

    public override set displayedValue(v: ITextBuilder) {
        if (!v) {
            this.innerValue = null;
            return;
        }

        const enumDesc = this._data.descriptions.find((e) => e.Description === v);
        this.innerValue = enumDesc?.IntValue.toString();
    }

    /**
     * Označí prvek comboboxu
     * @param v Prvek co má být označen; atribut Name
     */
    public setDisplayedValueByName(v: ITextBuilder): void {
        if (!v) {
            this.innerValue = null;
            return;
        }

        const enumDesc = this._data.descriptions.find((e) => e.Name === v);
        this.innerValue = enumDesc?.IntValue.toString();
    }

    /**
     * Property, zda má být zobrazen prázdný záznam
     */
    private get _showEmptyOption(): boolean {
        return this.__showEmptyOption;
    }

    /**
     * Nastavuje, zda má být zobrazen prázdný prvek.
     * POZOR! Nemělo by se nastavovat přímo ručně, ale aktualizovat přes metodu _updateShowEmptyOption
     * @param value True, pokud má být prázdný prvek zobrazen
     */
    private set _showEmptyOption(value: boolean) {
        this.__showEmptyOption = value;
        this._emptyOption.classList.toggle(bemClass.element("empty-option", "hidden"), !value);
    }

    public override get isEditing(): boolean {
        return super.isEditing;
    }

    public override set isEditing(value: boolean) {
        super.isEditing = value;
        this._updateAvailableOptions();
    }

    public override get required(): boolean {
        return super.required;
    }

    public override set required(value: boolean) {
        super.required = value;
        this._updateShowEmptyOption();
    }

    public override get visible(): boolean {
        return super.visible;
    }

    public override set visible(value: boolean) {
        const oldVal = this.visible;
        super.visible = value;

        if (value && !oldVal)
            this._updateAvailableOptions();
    }

    public get multiEditActive(): boolean {
        return this._multiEditActive;
    }

    public set multiEditActive(value: boolean) {
        this._multiEditActive = value;
        this._touched = false;
        this. _valueState = FakeComponentValueState.notSet;
        if (this._multiEditActive) {
            this._addMultiEditOptionIfNotPresent();
        } else {
            this._touched = false;
            this._removeMultiEditOptionIfPresent();
        }
    }

    /**
     * Nastaví příznak, že byla nastavena hodnota v průběhu aktivní hromadné editace (uživatelem nebo programově).
     * Pokud je nastaveno na true v průběhu hromadné editace, odebere možnost určenou pro indikaci aktivní hromadné editace.
     * @param value Hodnota k nastavení. Hodnota true se nastaví pouze pokud je aktivní hromadná editace.
     */
    private set _touched(value: boolean) {
        this.__touched = this.multiEditActive && value;
    }

    /**
     * Popis viz {@link __touched}.
     */
    private get _touched(): boolean {
        return this.__touched;
    }

    //#endregion Properties

    //#region Constructors

    // povinne atributy: attrName, enumName, locKey
    constructor(params: ControlParams) {
        super(params);

        this.attrName = this.getDataAttr("attrname");

        this.element.classList.add(bemClass(), "ComboBox", "icon");
        this._enumName = this.getDataAttr("enumName");
        //if (!this.enumName) { logger.error("Nebyl zadan povinny atribut enumName"); }//vypisovalo se to na prihlasovaci obrazovce

        this._hideValues = this.getDataAttr("hideValues");

        this._createInput();
        this._setListeners();
        this._setCaptionReference(this.innerElement);
    }
    //#endregion Constructors

    //#region Methods

    /**
     * Inicializacni metoda pro asynchronni docteni enumu
     */
    public async initialize(): Promise<void> {
        if (this._enumName) {
            await this._loadEnumData();

            this._checkCollection();

            // hack, aby neproblikavala hodnota, pokud je prazdna
            if (this._hasBeforeLoadValue && this._beforeLoadValue == null) {
                this.innerElement.style.visibility = "hidden";
            }

            for (const item of this._data.descriptions)
                this._appendOption(item);

            // Pokud je hodnota nastavena driv nez je nacten combobox ulozi se do docasne promenne, ze ktere se po nacteni nacte
            this._isLoaded = true;

            if (this._hasBeforeLoadValue) {
                this.controlValue = this._beforeLoadValue;
                this._hasBeforeLoadValue = false;
            }

            this._updateAvailableOptions();

            this.innerElement.style.visibility = "";
        }
    }

    private async _loadEnumData(): Promise<void> {
        this._data = await OWManager.getEnum(this._enumName);
        await this._setLocalization();
    }

    private async _setLocalization(): Promise<void> {
        // všechny položky <option> stejně musíme vytvořit, takže si načteme jejich překlady dopředu, čímž předejdeme problémům s asynchronním vytvářením položek později
        const locPromises = this._data.descriptions.map(async item => [item.IntValue, await this._data.getValue(item.IntValue).getLocalizedName()] as [number, string]);
        this._enumLocalizations = new Map(await Promise.all(locPromises));
    }

    // Vytvori select element
    private _createInput(): void {
        this.innerElement = document.createElement("select");
        this.innerElement.classList.add(bemClass.element("select"));
        this.disabledFixElement = document.createElement("div");
        this.disabledFixElement.classList.add(bemClass.element("disabled-fix"));

        //pridani prazdnych option
        this._placeholderOption.text = "";
        this._placeholderOption.classList.add(bemClass.element("placeholder-option"));
        if (this._placeholderLocKey) {
            Localization.addLocData(this._placeholderOption, this._placeholderLocKey, "text");
        }
        this.innerElement.appendChild(this._placeholderOption);

        this._emptyOption.text = "";
        this._emptyOption.classList.add(bemClass.element("empty-option"));
        this.innerElement.appendChild(this._emptyOption);

        this._showEmptyOption = true;
        this._updateEmptyState();

        if (this.getDataAttr("lockey"))
            Localization.addLocDescData(this.innerElement, this.getDataAttr("lockey"), "title");

        this.element.appendChild(this.disabledFixElement);
        this.element.appendChild(this.innerElement);
    }

    private _updateEmptyState(): void {
        const actualValue = this.innerValue;
        const isMultiEdit = this._multiEditActive && actualValue == ComboBox.MULTI_EDIT_OPTION_VALUE;
        const isEmpty = !isMultiEdit && [ComboBox.EMPTY_OPTION_VALUE, ComboBox.PLACEHOLDER_OPTION_VALUE, "", null, undefined].includes(actualValue);
        this.innerElement?.classList.toggle(bemClass.element("select", "is-empty"), !isMultiEdit && isEmpty);
        if (isEmpty && actualValue != ComboBox.PLACEHOLDER_OPTION_VALUE) {
            this.innerElement.value = ComboBox.PLACEHOLDER_OPTION_VALUE;
        }
    }

    private _updateAvailableOptions(): void {
        if (this.element == null) // disposed
            return;

        // Aktualizujeme viditelné hodnoty podle stavu isEditing (musí být zároveň visible a enabled)
        if (this._hiddenValues) {
            if (this.isEditing && this.visible && this.enabled) {
                const oldSelElement = this.innerElement.querySelector(":checked");
                this._hiddenValues.forEach((element) => {
                    const oldChild = this.innerElement.querySelector(`[value='${element.IntValue}']`); // `
                    if (oldChild) {
                        if (oldSelElement == oldChild) {
                            this.controlValue = null;
                            this.valueChanged.trigger(this, { Sender: this });
                        }
                        this.innerElement.removeChild(oldChild);
                    }
                });
            } else {
                const previousValue = this.innerValue;

                for (const element of this._hiddenValues)
                    this._appendOption(element);

                this.innerValue = previousValue; // Přidání prvku do kolekce způsobí reset, takže je nutné hodnotu nastavit zpět
            }
        }

        this._updateShowEmptyOption();
        this._updateEmptyState();
    }

    /**
     * Vytvoří a přidá option element do ComboBoxu
     * @param enumItem
     * TODO: udelat pres innerHTML, at je to vytvareni rychlejsi
     */
    private _appendOption(enumItem: EnumDescription): void {
        if (this.innerElement.querySelector(`[value='${enumItem.IntValue}']`))
            return;

        const option = document.createElement("option");
        option.value = enumItem.IntValue + "";

        // nastavení hodnoty musí synchronní (a ne přes Localization.addLoc*), protoze v Safari, pokud je nastavena hodnota selectu null, tak pri jakekoli zmene podrizene option,
        // ho automaticky nastavi jako svou hodnotu aniz by rekl, ze neco takoveho udelal
        // takze je treba lokalizace doplnit tady, jeste pred nastaveni hodnot
        option.textContent = this._enumLocalizations.get(enumItem.IntValue);

        //Localization.addLocTxtData(option, element.LocKey);

        this.innerElement.add(option);
    }

    /** Checkovani hodnot oproti hideValues */
    private _checkCollection(): void {
        if (this._hideValues) {
            let unwantArr: string[] = this._hideValues.split(/\s*;\s*/); //odstranime pripadne mezery okolo stredniku
            // vymazani pripadnych prazdnych prvku z pole
            unwantArr = unwantArr.filter(Boolean);

            // Vyfiltruje hodnoty, které nemají být v editaci vidět
            this._hiddenValues = this._data.descriptions.filter((el) => {
                return unwantArr.indexOf(el.Name) >= 0;
            });

            this._hiddenValues.forEach((item) => {
                this._hiddenIds.push(item.IntValue);
            });
        }

        this._data.descriptions.forEach((item) => {
            this._comboListIds.push(item.IntValue);
        });
    }

    private _setListeners(): void {
        let click = 0;
        this._events.addListener(this.innerElement, "focus", e => {
            this.getFocus.trigger(this, e);
            if (this.enabled)
                this.element.classList.add("focused");
        });

        //TODO: testnout na BB + pripadne osefovat vyber pomoci klavesnice

        this._events.addListener(this.innerElement, "click", (e) => {
            if (this.enabled) {
                ++click;
                this.element.classList.toggle("focused", !!(click % 2));
            }
            e.stopPropagation();
        });

        this._events.addListener(this.innerElement, "mousedown", () => {
            // TODO [MB] 30215 - kontrola left mouse button
            this._evaluateMultiEditAfterMouseDown();
        });

        this._events.addListener(this.innerElement, "blur", e => {
            this.lostFocus.trigger(this, e);
            this.element.classList.remove("focused");
            click = 0;
        });

        this._events.addListener(this.element, "change", () => this._updateEmptyState());
        this._events.addListener(this.innerElement, "change", () => {
            this._updateEmptyState();
            this.validate();
            this.valueChanged.trigger(this, { Sender: this });
            // Pokud je vybrána jiná hodnota než "Více hodnot", tak se "Více hodnot" odstraní.
            if (this.innerValue !== ComboBox.MULTI_EDIT_OPTION_VALUE) {
                this._removeMultiEditOptionIfPresent();
            }
        });

        this._events.addListener(this.disabledFixElement, "click", () => {
            if (this.enabled && this.interactive)
                this.innerElement.click();
        });
    }

    /**
     * Vrací Name označeného prvku
     */
    public getSelectedItemValue(): string {
        return this._getSelectedEnum()?.getName();
    }

    /**
     * Nastaví prvky comboboxu
     * @param data Data přidávaná do comboboxu
     */
    public async setData(data: EnumType): Promise<void> {
        this._data = data;
        await this._setLocalization();
        for (const element of this._data.descriptions)
            await this._appendOption(element);
    }

    /**
     * Podle Required a IsEditing nastavuje, zda má být zobrazen prázdný prvek
     */
    private _updateShowEmptyOption(): void {
        const oldShowEmptyOptionValue = this._showEmptyOption;
        const previousValue = this.innerValue;
        // Pokud je pole povinné a je v editaci, skrýt prázdný záznam
        this._showEmptyOption = !(this.isEditing && this.required);
        // Pokud se přidává/odebírá prázdný prvek, je potřeba obnovit selectedIndex
        // vnitřního selectu, jinak se selectedIndex ztrácí
        if (oldShowEmptyOptionValue !== this._showEmptyOption) {
            this.innerValue = previousValue;
        }
    }

    /**
     * Přidá a rovnou vybere možnost indikující více hodnot v hromadné editaci, pokud ještě přidaná není.
     * Zároveň přidá popisek vysvětlující tuto hodnotu.
     */
    private _addMultiEditOptionIfNotPresent(): void {
        if (this._multiEditOptionActive) {
            return;
        }

        Localization.getLocalizationItem(FAKE_COMPONENT_VALUE_LOC_KEY).then((localizationItem) => {
            this._multiEditOption.textContent = localizationItem.primaryText;
            this.innerElement.setAttribute("title", localizationItem.secondaryText);
            this.innerElement.prepend(this._multiEditOption);
            this.innerValue = ComboBox.MULTI_EDIT_OPTION_VALUE;
            this._multiEditOptionActive = true;
            this._updateEmptyState();
        });
    }

    /**
     * Odebere možnost indikující více hodnot v hromadné editaci.
     * Zároveň odstraní popisek vysvětlující tuto hodnotu.
     */
    private _removeMultiEditOptionIfPresent(): void {
        if (!this._multiEditOptionActive) {
            return;
        }
        this.innerElement.removeAttribute("title");
        this._multiEditOption?.remove();
        this._multiEditOptionActive = false;
        this._updateEmptyState();
    }

    /**
     * Vyhodnotí kliknutí na ComboBox z pohledu hromadné editace.
     * Pokud je hromadná editace aktivní a uživatel kliknul poprvé,
     * odstraní možnost indikující hromadnou editaci.
     */
    private _evaluateMultiEditAfterMouseDown(): void {
        this._touched = true;
    }

    /**
     * ComboBox používá select element, u kterého není možné programově otevřít nabídku možností.
     * Proto se provede pouze nastavení zaměření a nabídku si musí uživatel otevřít sám.
     */
    public override startEdit(): void {
        super.startEdit();
    }

    /**
     * Vrátí Enum, který patří vybrané možnosti nebo null.
     */
    private _getSelectedEnum(): Enum {
        const optionValue = this.innerElement.options.item(this.innerElement.selectedIndex)?.value;
        const numberValue = optionValue == null ? null : Number(optionValue);
        if (numberValue == null || !Number.isFinite(numberValue) || numberValue < 0) {
            return null;
        }
        return this._data.getValue(optionValue) as Enum;
    }

//#endregion Methods
}
